import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface PublicPackage {
  id: string;
  slug: string;
  name: string;
  shortDescription: string | null;
  description: string;
  price: number;
  oldPrice: number | null;
  isPopular: boolean;
  thumbnailUrl: string | null;
  featuresJson: string[];
  accessDays: number | null;
  includesLive?: boolean;
}

export interface PublicPackageCourse {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  thumbnailUrl: string | null;
  durationMin: number | null;
  sectionCount: number;
}

export interface PublicPackageDetail extends PublicPackage {
  includesLive: boolean;
  courses: PublicPackageCourse[];
}

export interface PublicCourse {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  thumbnailUrl: string | null;
  durationMin: number | null;
  difficulty: string | null;
  price: number;
  oldPrice: number | null;
  isFeatured: boolean;
  category: { name: string; slug: string } | null;
  enrollCount: number;
  sectionCount: number;
}

export interface PublicLessonLite {
  id: string;
  title: string;
  type: "VIDEO" | "LIVE" | "PDF" | "QUIZ" | "TEXT";
  durationMin: number | null;
  isFreePreview: boolean;
  sortOrder: number;
}

export interface PublicSectionWithLessons {
  id: string;
  title: string;
  sortOrder: number;
  lessons: PublicLessonLite[];
}

export interface PublicInstructor {
  id: string;
  firstName: string;
  lastName: string;
  bio: string | null;
  avatarUrl: string | null;
}

export interface PublicCoursePackage {
  id: string;
  slug: string;
  name: string;
  shortDescription: string | null;
  price: number;
  oldPrice: number | null;
  isPopular: boolean;
  accessDays: number | null;
  featuresJson: string[];
}

export interface PublicCourseDetail {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string;
  thumbnailUrl: string | null;
  durationMin: number | null;
  difficulty: string | null;
  language: string;
  price: number;
  oldPrice: number | null;
  isFeatured: boolean;
  publishedAt: string | null;
  category: { id: string; name: string; slug: string } | null;
  instructor: PublicInstructor | null;
  sections: PublicSectionWithLessons[];
  packages: PublicCoursePackage[];
  enrollCount: number;
}

export interface PublicTestimonial {
  id: string;
  authorName: string;
  authorRole: string | null;
  text: string;
  rating: number;
}

export interface PublicSettings {
  site_phone?: string;
  site_whatsapp?: string;
  site_email?: string;
  site_address?: string;
  social_instagram?: string;
  social_youtube?: string;
  social_facebook?: string;
  social_twitter?: string;
  site_favicon_url?: string;
  site_logo_header_url?: string;
  site_logo_footer_url?: string;
  site_logo_alt?: string;
  tracking_google_ads_id?: string;
  tracking_meta_pixel_id?: string;
  /** JSPS sınav tarihi (ISO 8601). Boş/yoksa geri sayım gösterilmez. */
  jsps_exam_at?: string;
}

export interface PublicHeroSlide {
  id: string;
  title: string | null;
  subtitle: string | null;
  description: string | null;
  imageUrl: string | null;
  badgeText: string | null;
  primaryCtaText: string | null;
  primaryCtaUrl: string | null;
  secondaryCtaText: string | null;
  secondaryCtaUrl: string | null;
  sortOrder: number;
}

export interface LegalPageData {
  slug: string;
  title: string;
  contentMd: string;
  version: string;
  effectiveAt: string;
  updatedAt: string;
}

export const usePackages = () =>
  useQuery({
    queryKey: ["packages"],
    queryFn: () => api.get<PublicPackage[]>("/api/packages"),
  });

export const usePackage = (slug: string | undefined) =>
  useQuery({
    queryKey: ["package", slug],
    queryFn: () => api.get<PublicPackageDetail>(`/api/packages/${slug}`),
    enabled: !!slug,
  });

export const useCourses = (filters?: { kategori?: string; q?: string }) =>
  useQuery({
    queryKey: ["courses", filters],
    queryFn: () => api.get<PublicCourse[]>("/api/courses", { query: filters }),
  });

export const useCourse = (slug: string | undefined) =>
  useQuery({
    queryKey: ["course", slug],
    queryFn: () => api.get<PublicCourseDetail>(`/api/courses/${slug}`),
    enabled: !!slug,
  });

export const useTestimonials = () =>
  useQuery({
    queryKey: ["testimonials"],
    queryFn: () => api.get<PublicTestimonial[]>("/api/testimonials"),
  });

export const usePublicSettings = () =>
  useQuery({
    queryKey: ["settings", "public"],
    queryFn: () => api.get<PublicSettings>("/api/settings/public"),
    staleTime: 5 * 60 * 1000,
    // Refetch sırasında \xf6nceki data'yı koru — logo/branding flicker'ı engellenir
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });

export const useLegalPage = (slug: string) =>
  useQuery({
    queryKey: ["legal", slug],
    queryFn: () => api.get<LegalPageData>(`/api/legal/${slug}`),
  });

export interface PublicBlogPostListItem {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  coverImageUrl: string | null;
  publishedAt: string | null;
  category: { name: string; slug: string } | null;
  tagsJson: string[] | null;
}

export interface PublicBlogPostDetail {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string;
  coverImageUrl: string | null;
  publishedAt: string | null;
  viewCount: number;
  author: { firstName: string; lastName: string; avatarUrl: string | null } | null;
  category: { name: string; slug: string } | null;
  tagsJson: string[] | null;
}

export const useBlogPosts = (filters?: { kategori?: string; etiket?: string }) =>
  useQuery({
    queryKey: ["blog", "list", filters],
    queryFn: () => api.get<PublicBlogPostListItem[]>("/api/blog", { query: filters }),
  });

export interface PublicAnnouncement {
  id: string;
  title: string;
  body: string;
  level: "INFO" | "WARNING" | "SUCCESS";
  showOnHome: boolean;
  showInPanel: boolean;
}

export const useActiveAnnouncements = () =>
  useQuery({
    queryKey: ["announcements", "active"],
    queryFn: () => api.get<PublicAnnouncement[]>("/api/announcements/active"),
  });

export const useBlogPost = (slug: string | undefined) =>
  useQuery({
    queryKey: ["blog", "post", slug],
    queryFn: () => api.get<PublicBlogPostDetail>(`/api/blog/${slug}`),
    enabled: !!slug,
  });

export const useHeroSlides = () =>
  useQuery({
    queryKey: ["hero-slides"],
    queryFn: () => api.get<PublicHeroSlide[]>("/api/hero-slides"),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
